import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import {
  ApiError,
  AutoPaySettingVM,
  DocumentService,
  EntityType,
  OrganizationService,
  OrganizationSubscriptionVM,
  PaidFlowTypesEnum,
  PayDueAccountStatusEnum,
  PaymentDueAccountVM,
  PaymentMethodTypesEnum,
  PaymentMethodVM,
  PaymentService,
} from "@propertelligent/client-api";
import Card from "../../../newComponents/card";
import RightSideModal from "../../../newComponents/Modal/right-side-modal";
import { ManageAutoPayForm } from "../../../tenantComponent/components/ManageAutoPay";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import { AccessLevelEnum } from "@propertelligent/client-api";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import EmptyState from "../../../newComponents/emptystate";
import CurrencyTooltip from "../../currencytooltip/CurrencyTooltip";
import SimpleSpinner from "../../loader";
import NoAccess from "../../../pages/NoAccess";
import { usePermissions } from "../../../hooks/usePermissions";
import { UserLoginData } from "../../../components/common/charts/Enums";
import { GetImages } from "../../../utils/GetImages";
import { convertStreamAndDownloadBymimeTypes } from "../../../utils/helper";
import apiHelper from "../../../utils/apiHelper";
import PaymentSummery from "../../../newComponents/payments/PaymentSummery";
import "./Subscription.css";

// Subscription invoices in either of these two statuses still owe a balance and are eligible to
// be posted against, mirroring the same statuses PaymentMethodService's payer-side delete guard
// (bug/payer-account-due-delete) treats as "open".
const OPEN_INVOICE_STATUSES = [
  PayDueAccountStatusEnum.Due,
  PayDueAccountStatusEnum.PaidPartially,
];

type FormattedValues = Record<string, string | number | undefined>;

interface AutoPayFormValues {
  isEnabled: string;
  plaidAccountId: string;
}

interface PlaidAccountOption {
  Id: number;
  Name: string;
}

const statusClassMap: Record<number, string> = {
  [PayDueAccountStatusEnum.Due]: "status-due",
  [PayDueAccountStatusEnum.Paid]: "status-paid",
  [PayDueAccountStatusEnum.PaidPartially]: "status-partially-paid",
  [PayDueAccountStatusEnum.WaivedPartially]: "status-waived-partially",
  [PayDueAccountStatusEnum.Waived]: "status-waived",
};

const formattedValue = (
  item: { formatted?: Record<string, unknown> | null },
  key: string,
) => item.formatted?.[key] as string | number | undefined;

const MySubscriptionComponent = () => {
  const [isAutoPayOpen, setIsAutoPayOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] =
    useState<OrganizationSubscriptionVM | null>(null);
  const [currentInvoice, setCurrentInvoice] =
    useState<PaymentDueAccountVM | null>(null);
  const [pastInvoices, setPastInvoices] = useState<PaymentDueAccountVM[]>([]);
  const [plaidAccounts, setPlaidAccounts] = useState<PlaidAccountOption[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodVM[]>([]);
  const [existingAutoPaySetting, setExistingAutoPaySetting] =
    useState<AutoPaySettingVM | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoPaySaveError, setAutoPaySaveError] = useState<string | null>(null);
  const [isPostPaymentOpen, setIsPostPaymentOpen] = useState(false);
  const [postPaymentError, setPostPaymentError] = useState<string | null>(
    null,
  );

  const navigate = useNavigate();
  const organizationId = Number(
    sessionStorage.getItem(UserLoginData.organization_id),
  );
  const { canWrite } = usePermissions();
  const canManageAutoPay = canWrite("Subscription");
  const canManagePaymentAccounts = canWrite("PaymentAccounts");
  const autoPayFormId = "subscription-manage-auto-pay-form";
  const autoPayMethods = useForm<AutoPayFormValues>({
    mode: "onChange",
    defaultValues: { isEnabled: "true", plaidAccountId: "" },
  });

  const postPaymentFormId = "subscription-post-payment-form";
  const postPaymentMethods = useForm({
    mode: "onChange",
    defaultValues: {
      paymentMethodTypeId: "",
      PaymentModeId: "",
      date: new Date().toISOString().split("T")[0],
    },
  });

  // Every invoice still owing a balance -- the current one (if any) plus any past invoice that
  // never got fully settled -- is eligible for Post Payment. When there's exactly one, a single
  // side-drawer settles it directly; when there's more than one, the landlord is routed to the
  // existing generic multi-select Payables Post Payment page (which already fully supports the
  // Propertelligent-subscription Plaid-only guard via PaymentSummery).
  const openDueInvoices = useMemo(
    () =>
      [currentInvoice, ...pastInvoices].filter(
        (invoice): invoice is PaymentDueAccountVM =>
          !!invoice &&
          OPEN_INVOICE_STATUSES.includes(
            invoice.paymentDueAccountStatusId as PayDueAccountStatusEnum,
          ),
      ),
    [currentInvoice, pastInvoices],
  );

  const fetchSubscriptionData = async () => {
    setLoading(true);
    setError(null);

    try {
      const subscriptionResponse =
        await OrganizationService.getApiOrganizationSubscriptionDetails(
          organizationId,
        );
      setSubscription(subscriptionResponse.success ?? null);

      try {
        const currentInvoiceResponse =
          await OrganizationService.getApiOrganizationCurrentSubscriptionInvoice(
            organizationId,
          );
        setCurrentInvoice(currentInvoiceResponse.success ?? null);
      } catch (requestError) {
        if ((requestError as ApiError)?.status !== 404) {
          throw requestError;
        }
        setCurrentInvoice(null);
      }

      const pastInvoicesResponse =
        await OrganizationService.getApiOrganizationPastSubscriptionInvoice(
          organizationId,
        );
      setPastInvoices(pastInvoicesResponse.success ?? []);

      try {
        const paymentMethodsResponse =
          await PaymentService.getApiPaymentMethodOrganizationlist(
            organizationId,
          );
        const methods = paymentMethodsResponse.success ?? [];
        setPaymentMethods(methods);
        setPlaidAccounts(
          methods
            .filter(
              (method) =>
                method.paymentMethodTypeId === PaymentMethodTypesEnum.Plaid,
            )
            .map((method) => ({
              Id: method.id ?? 0,
              Name: method.accountName || `Account #${method.id}`,
            }))
            .filter((method) => method.Id > 0),
        );
      } catch (requestError) {
        console.error(
          "Failed to load payment accounts for Auto Pay:",
          requestError,
        );
        setPaymentMethods([]);
        setPlaidAccounts([]);
      }

      try {
        const autoPayResponse =
          await PaymentService.getApiAutoPaySettingOrganizationlist(
            organizationId,
          );
        setExistingAutoPaySetting(autoPayResponse.success?.[0] ?? null);
      } catch (requestError) {
        console.error("Failed to load Auto Pay settings:", requestError);
        setExistingAutoPaySetting(null);
      }
    } catch (requestError) {
      console.error("Failed to load subscription data:", requestError);
      setError(
        "Unable to load subscription information. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!Number.isInteger(organizationId) || organizationId <= 0) {
      setError("Organization ID not found. Please log in again.");
      setLoading(false);
      return;
    }

    void fetchSubscriptionData();
  }, [organizationId]);

  const openAutoPayDrawer = () => {
    setAutoPaySaveError(null);
    autoPayMethods.reset({
      isEnabled: existingAutoPaySetting?.isEnabled === false ? "false" : "true",
      plaidAccountId: existingAutoPaySetting?.plaidAccountId
        ? String(existingAutoPaySetting.plaidAccountId)
        : "",
    });
    setIsAutoPayOpen(true);
  };

  const handleSaveAutoPay = async (values: AutoPayFormValues) => {
    const payload: AutoPaySettingVM = {
      id: existingAutoPaySetting?.id,
      entityId: organizationId,
      entityTypeId: EntityType.Organization,
      isEnabled: values.isEnabled === "true",
      plaidAccountId: values.plaidAccountId
        ? Number(values.plaidAccountId)
        : undefined,
    };

    const apiCall = existingAutoPaySetting?.id
      ? PaymentService.putApiAutoPaySetting
      : PaymentService.postApiAutoPaySetting;

    try {
      const response = await apiHelper(
        apiCall,
        {
          title: existingAutoPaySetting?.id
            ? "Auto Pay Updated"
            : "Auto Pay Enabled",
          successMessage: "Auto Pay settings updated successfully",
          failureMessage: "Unable to update Auto Pay settings",
          showNotification: true,
        },
        payload,
      );

      if (!response?.data?.success) {
        setAutoPaySaveError("Unable to update Auto Pay settings. Please review the selected account and try again.");
        return;
      }

      setExistingAutoPaySetting(response.data.success);
      setIsAutoPayOpen(false);
    } catch (requestError) {
      console.error("Failed to save Auto Pay settings:", requestError);
      setAutoPaySaveError("Unable to update Auto Pay settings. Please try again.");
    }
  };

  // Exactly one open invoice -> settle it directly in a side drawer. More than one -> hand off to
  // the existing generic multi-select Payables Post Payment page, keyed by the shared
  // Propertelligent-subscription payee account so it lists every open invoice for this org.
  const handlePostPaymentClick = () => {
    if (openDueInvoices.length === 0) {
      return;
    }

    if (openDueInvoices.length > 1) {
      const payeeAccountId = openDueInvoices[0].payeeAccountId;
      navigate(
        `/payment/settle-payables-payment/${payeeAccountId}?pai=${payeeAccountId}&paymentType=${PaidFlowTypesEnum.Payable}`,
      );
      return;
    }

    setPostPaymentError(null);
    postPaymentMethods.reset({
      paymentMethodTypeId: "",
      PaymentModeId: "",
      date: new Date().toISOString().split("T")[0],
    });
    setIsPostPaymentOpen(true);
  };

  const handleSubmitPostPayment = async (formData: {
    paymentMethodTypeId: string;
    PaymentModeId: string;
    date: string;
  }) => {
    const dueInvoice = openDueInvoices[0];
    if (!dueInvoice?.id) {
      return;
    }

    setPostPaymentError(null);
    try {
      const response = await apiHelper(
        PaymentService.putApiPaymentDueAccountPay,
        {
          successMessage: "Payment Posted Successfully",
          failureMessage: "Unable to post payment",
          showNotification: true,
        },
        {
          payDueAccountId: dueInvoice.id,
          amount: dueInvoice.amount,
          paymentMethodTypeId: Number(formData.paymentMethodTypeId),
          fromPaymentMethodId: Number(formData.PaymentModeId),
          paymentDate: new Date(formData.date).toISOString(),
        },
      );

      if (!response?.data?.success) {
        setPostPaymentError(
          "Unable to post payment. Please review the selected account and try again.",
        );
        return;
      }

      setIsPostPaymentOpen(false);
      await fetchSubscriptionData();
    } catch (requestError) {
      console.error("Failed to post subscription payment:", requestError);
      setPostPaymentError("Unable to post payment. Please try again.");
    }
  };

  const downloadInvoice = async (invoice: PaymentDueAccountVM) => {
    if (!invoice.receiptDocumentId) {
      return;
    }

    const response = await apiHelper(
      DocumentService.getApiDocumentBase641,
      { showNotification: true, failureMessage: "Unable to download invoice" },
      invoice.receiptDocumentId,
    );
    convertStreamAndDownloadBymimeTypes(
      response?.data?.success,
      `Invoice-${invoice.referenceNumber || invoice.receiptDocumentId}.pdf`,
    );
  };

  if (loading) {
    return <SimpleSpinner myClassName="Icon50" isFullPage />;
  }

  if (error) {
    return (
      <div className="subscription-container container-fluid">
        <EmptyState
          heading="Subscription unavailable"
          bodytext={error}
          icon={GetImages.emptyStateData}
        />
      </div>
    );
  }

  const currentInvoiceFormatted = (currentInvoice?.formatted ??
    {}) as FormattedValues;
  const autoPayUnavailable = plaidAccounts.length === 0;

  return (
    <PermissionGuard
      permission="Subscription"
      level={AccessLevelEnum.ReadOnly}
      fallback={<NoAccess />}
    >
      <div className="subscription-container container-fluid">
        <div className="subscription-header">
          <div>
            <h2 className="H2">My Subscription</h2>
            <p>Review your plan, invoices, and automatic payment settings.</p>
          </div>
          <div className="d-flex gap-2">
            <SimpleButton
              label="Post Payment"
              onClick={handlePostPaymentClick}
              isDisabled={
                openDueInvoices.length === 0 || !canManageAutoPay
              }
              testId="post-subscription-payment"
            />
            <SimpleButton
              label="Manage Auto Pay"
              onClick={openAutoPayDrawer}
              isDisabled={autoPayUnavailable || !canManageAutoPay}
              testId="manage-subscription-auto-pay"
            />
          </div>
        </div>

        {autoPayUnavailable && (
          <div className="subscription-notice" role="status">
            <span>
              Connect a Plaid payment account to continue. Subscription Auto Pay
              also requires the platform to have a connected Plaid account.
            </span>
            {canManagePaymentAccounts && (
              <button
                type="button"
                onClick={() => navigate("/organization/new-payment-accounts")}
              >
                Manage payment accounts
              </button>
            )}
          </div>
        )}
        <div className="subscription-overview">
          <div className="subscription-summary-grid">
            <SubscriptionSummaryCard
              icon={GetImages.SubscriptionTrialIcon}
              label="Subscription"
              value={
                formattedValue(subscription ?? {}, "SubscriptionType") ?? "—"
              }
            />
            <SubscriptionSummaryCard
              icon={GetImages.subscriptionCalendarIcon}
              label="Expires on"
              value={
                formattedValue(
                  subscription ?? {},
                  "ExpirationDateDateString",
                ) ?? "—"
              }
            />
            <SubscriptionSummaryCard
              icon={GetImages.subscriptionCalendarIcon}
              label="Billing cycle"
              value={formattedValue(subscription ?? {}, "BillingCycle") ?? "—"}
            />
            <SubscriptionSummaryCard
              icon={GetImages.subscriptionCalendarIcon2}
              label="Auto-renewal"
              value={subscription?.autoRenewal ? "On" : "Off"}
            />
          </div>

          <Card className="current-invoice-card">
            <div className="invoice-header">
              <span className="section-subtitle">Current Invoice</span>
              {currentInvoice && (
                <span
                  className={`status-badge ${statusClassMap[currentInvoice.paymentDueAccountStatusId ?? -1] ?? "status-pending"}`}
                >
                  {currentInvoiceFormatted.PaymentDueAccountStatusId ??
                    "Unknown"}
                </span>
              )}
            </div>

            {currentInvoice ? (
              <dl className="invoice-details">
                <div>
                  <dt>Due date</dt>
                  <dd>{currentInvoiceFormatted.DueDateDateString ?? "—"}</dd>
                </div>
                <div>
                  <dt>Amount due</dt>
                  <dd className="total-amount">
                    <CurrencyTooltip
                      content={currentInvoiceFormatted.AmountRaw}
                      formatted={currentInvoiceFormatted.Amount}
                    >
                      <span>{currentInvoiceFormatted.Amount ?? "—"}</span>
                    </CurrencyTooltip>
                  </dd>
                </div>
                <div>
                  <dt>Invoice number</dt>
                  <dd>
                    {currentInvoice.referenceNumber
                      ? `#${currentInvoice.referenceNumber}`
                      : "—"}
                  </dd>
                </div>
                {currentInvoice.receiptDocumentId && (
                  <div className="invoice-download">
                    <dt className="visually-hidden">Invoice document</dt>
                    <dd>
                      <button
                        type="button"
                        className="download-btn"
                        onClick={() => void downloadInvoice(currentInvoice)}
                      >
                        Download invoice
                      </button>
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <EmptyState
                heading="No active invoice"
                bodytext="There is no active invoice for your subscription."
                icon={GetImages.emptyStateData}
              />
            )}
          </Card>
        </div>

        <Card className="billing-history-card">
          <div className="table-header">
            <div>
              <h5>Billing History</h5>
              <p>Past-due, settled, and waived subscription invoices.</p>
            </div>
          </div>
          {pastInvoices.length > 0 ? (
            <div className="table-responsive">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th scope="col">Invoice no.</th>
                    <th scope="col">Billing date</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Status</th>
                    <th scope="col">
                      <span className="visually-hidden">Action</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pastInvoices.map((invoice) => {
                    const formatted = (invoice.formatted ??
                      {}) as FormattedValues;
                    return (
                      <tr key={invoice.id ?? invoice.referenceNumber}>
                        <td>
                          {invoice.referenceNumber
                            ? `#${invoice.referenceNumber}`
                            : "—"}
                        </td>
                        <td>{formatted.DueDateDateString ?? "—"}</td>
                        <td>
                          <CurrencyTooltip
                            content={formatted.AmountRaw}
                            formatted={formatted.Amount}
                          >
                            <span>{formatted.Amount ?? "—"}</span>
                          </CurrencyTooltip>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${statusClassMap[invoice.paymentDueAccountStatusId ?? -1] ?? "status-pending"}`}
                          >
                            {formatted.PaymentDueAccountStatusId ?? "Unknown"}
                          </span>
                        </td>
                        <td className="billing-action">
                          {invoice.receiptDocumentId && (
                            <button
                              type="button"
                              className="download-btn"
                              onClick={() => void downloadInvoice(invoice)}
                            >
                              Download
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              heading="No billing history"
              bodytext="Settled subscription invoices will appear here."
              icon={GetImages.emptyStateData}
            />
          )}
        </Card>

        <RightSideModal
          isOpen={isAutoPayOpen}
          onClose={() => setIsAutoPayOpen(false)}
          title="Manage Auto Pay"
          buttonlabel="Save"
          formId={autoPayFormId}
          maxWidth={480}
          isDirty={autoPayMethods.formState.isDirty}
          isSubmitDisabled={
            autoPayMethods.formState.isSubmitting ||
            !autoPayMethods.formState.isDirty ||
            !autoPayMethods.formState.isValid
          }
        >
          <FormProvider {...autoPayMethods}>
            <ManageAutoPayForm
              formId={autoPayFormId}
              onSubmit={handleSaveAutoPay}
              accountOptions={plaidAccounts}
              infoText="Auto payment of subscription fees will begin on the next payment due date."
            />
            {autoPaySaveError && (
              <p className="subscription-form-error" role="alert">
                {autoPaySaveError}
              </p>
            )}
          </FormProvider>
        </RightSideModal>

        <RightSideModal
          isOpen={isPostPaymentOpen}
          onClose={() => setIsPostPaymentOpen(false)}
          title="Post Payment"
          buttonlabel="Pay"
          formId={postPaymentFormId}
          maxWidth={480}
          isDirty={postPaymentMethods.formState.isDirty}
          isSubmitDisabled={
            postPaymentMethods.formState.isSubmitting ||
            !postPaymentMethods.formState.isDirty ||
            !postPaymentMethods.formState.isValid
          }
        >
          <form
            id={postPaymentFormId}
            onSubmit={postPaymentMethods.handleSubmit(
              handleSubmitPostPayment,
            )}
          >
            <FormProvider {...postPaymentMethods}>
              <PaymentSummery
                totalAmount={String(openDueInvoices[0]?.amount ?? 0)}
                paymentMethods={paymentMethods}
                Payments={openDueInvoices.length === 1 ? openDueInvoices : []}
              />
            </FormProvider>
          </form>
          {postPaymentError && (
            <p className="subscription-form-error" role="alert">
              {postPaymentError}
            </p>
          )}
        </RightSideModal>
      </div>
    </PermissionGuard>
  );
};

const SubscriptionSummaryCard = ({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string | number;
}) => (
  <div className="subscription-summary-card">
    <div className="icon-box">
      <img src={icon} alt="" width={24} height={24} className="color-white-icon" />
    </div>
    <div>
      <div className="card-value">{value}</div>
      <div className="card-label">{label}</div>
    </div>
  </div>
);

export default MySubscriptionComponent;
